import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="adjustment-selector"
//
// Renders one select per product adjustment. The list of applicable
// configurations depends on the currently selected variant's option_values,
// so the controller filters options client-side whenever configurator-state
// notifies us that the variant changed.
export default class extends Controller {
  static outlets = ["configurator-state"];
  static targets = ["group", "select"];
  static values = {
    configurations: { type: Array, default: [] },
    selectedId: { type: Number, default: 0 }
  };

  connect() {
    this.refresh();
  }

  configuratorStateOutletConnected() {
    this.refresh();
  }

  variantChanged() {
    this.refresh();
  }

  change(event) {
    const id = Number(event.target.value) || null;
    if (this.hasConfiguratorStateOutlet) {
      this.configuratorStateOutlet.adjustmentIdChanged(id);
    }
  }

  refresh() {
    const variantOptionValueIds = new Set(this.currentVariantOptionValueIds());
    let pendingNotifyId;

    this.groupTargets.forEach((group) => {
      const adjustmentId = Number(group.dataset.adjustmentId);
      const select = group.querySelector("[data-adjustment-selector-target='select']");
      if (!select) return;

      const blankLabel = this.blankLabelFor(select);
      const desiredValue = select.value || (this.selectedIdValue ? String(this.selectedIdValue) : "");

      const applicableConfigs = this.configurationsValue.filter((config) =>
        config.adjustment_id === adjustmentId &&
        variantOptionValueIds.has(config.option_value_dependency_id)
      );

      this.repopulateSelect(select, blankLabel, applicableConfigs);

      if (applicableConfigs.length === 0) {
        group.hidden = true;
        if (desiredValue !== "") pendingNotifyId = null;
        select.value = "";
        return;
      }

      group.hidden = false;
      const stillApplicable = applicableConfigs.some((c) => String(c.id) === desiredValue);
      if (stillApplicable) {
        select.value = desiredValue;
      } else {
        select.value = "";
        if (desiredValue !== "") pendingNotifyId = null;
      }
    });

    // Initial preselection is consumed; subsequent refreshes rely on select.value.
    this.selectedIdValue = 0;

    if (pendingNotifyId !== undefined && this.hasConfiguratorStateOutlet) {
      this.configuratorStateOutlet.adjustmentIdChanged(pendingNotifyId);
    }
  }

  blankLabelFor(select) {
    const blank = select.querySelector("option[value='']");
    return blank ? blank.textContent : "";
  }

  repopulateSelect(select, blankLabel, configs) {
    select.innerHTML = "";
    const blank = document.createElement("option");
    blank.value = "";
    blank.textContent = blankLabel;
    select.appendChild(blank);
    configs.forEach((config) => {
      const option = document.createElement("option");
      option.value = String(config.id);
      option.textContent = config.value_with_description;
      select.appendChild(option);
    });
  }

  currentVariantOptionValueIds() {
    if (!this.hasConfiguratorStateOutlet) return [];
    const state = this.configuratorStateOutlet.readState();
    return (state.variant?.options || []).map((opt) => opt.option_value_id);
  }
}
